import { useState, useRef } from 'react';
import { Redirect, Route, useLocation } from 'react-router-dom';
import { deleteCookie, getCookie, getLocalStorage, setCookie, setLocalStorage } from './aaa';
import { getStore } from './ApplicationState';
import { useAuth } from './CustomHooks';
import { canShouldUserAutoSync } from './GlobalFunctions';
import SpinnerOverlay from './SpinnerOverlay';
import { isElementaryStudent, isStudent } from './UserManagementHelpers';
import { UserProfileType } from '../peekapak-types/DataProtocolTypes';
import { hasUserMadeChoice } from './SurveyChoiceHelpers';

interface Props {
  children: JSX.Element | JSX.Element[];
  requiredKeys?: string | string[];
  [key: string]: unknown;
}

export const AuthenticatedRoute = ({ children, requiredKeys, ...rest }: Props) => {
  const location = useLocation();
  // console.debug(
  //   `%cAuthenticated route render `,
  //   'background: blue; color: yellow',
  //   location.pathname
  // );
  const [isCheckingAuth, setIsCheckingAuth] = useState<boolean>(false);
  const auth = useAuth();
  const surveyRedirectCount = useRef(0);
  const lastSurveyRedirectPath = useRef<string | null>(null);

  if (auth.isUserLoggedInStateUnknown()) {
    //console.debug( `%cRoot login state unknown, attempting to reactivate`, 'background: #222; color: #bada55' )
    auth.reactivateUserSession();
    setIsCheckingAuth(true);
    return <SpinnerOverlay isShow={true} />;
  }

  if (auth.isUserLoggedInStateInTransition()) {
    auth.onLoggedInStateKnown(() => {
      setIsCheckingAuth(false);
    });
    return <SpinnerOverlay isShow={true} />;
  }

  if (isCheckingAuth) {
    return <SpinnerOverlay isShow={true} />;
  }

  if (auth.isUserSessionExpired()) {
    return (
      <Route
        render={({ location }) => (
          <Redirect
            to={{
              pathname: '/sessionExpired',
              state: { nextPathname: location.pathname },
            }}
          />
        )}
      />
    );
  }

  if (!auth.isUserLoggedIn() && !auth.isUserParentAccessStorybook()) {
    // doesn't seem like the user is logged in or using story book access, but let's check just to be sure

    // was this a parent story book access link?
    // if so, we need to redirect to the storybook
    const storybookAccessUri = getCookie('peekapak.storybookForParents');
    if (storybookAccessUri) {
      window.location.href = storybookAccessUri;
      return;
    }

    if (rest.location instanceof Location) {
      setCookie('peekapak.pathBeforeLoginRedirect', rest.location.pathname, 7);
    }

    return (
      <Route
        render={({ location }) => (
          <Redirect
            to={{
              pathname: '/membersOnlyRedirect',
              state: { nextPathname: location.pathname },
            }}
          />
        )}
      />
    );
  }

  const keysAsArray = requiredKeys ? (!Array.isArray(requiredKeys) ? [requiredKeys] : requiredKeys) : [];

  for (let i = 0; i < keysAsArray.length; i += 1) {
    if (!auth.isUserHasKey(keysAsArray[i])) {
      return <Route {...rest} render={(_) => <Redirect to={{ pathname: '/unauthorized' }} />} />;
    }
  }

  return performPreUseOperations();

  function performPreUseOperations() {
    const { userProfile } = getStore().getState().user;

    if (auth.isUserMissingSchool() && !isSkipCheckRouteForMissingSchool() && !isStudent(userProfile)) {
      return (
        <Route
          render={({ location }) => (
            <Redirect
              to={{
                pathname: '/moreInformation/school',
                state: { nextPathname: location.pathname },
              }}
            />
          )}
        />
      );
    }

    if (canShouldUserAutoSync() && !isSkipCheckRouteForAutoSync()) {
      const { user } = getStore().getState();
      const autoSyncClassroomsCompleted = getLocalStorage('autoSyncClassroomsCompleted');
      const autoSyncClassroomsErrored = getLocalStorage('autoSyncClassroomsErrored');
      if (autoSyncClassroomsCompleted !== 'true' && autoSyncClassroomsErrored !== 'true') {
        return (
          <Route
            render={({ location }) => (
              <Redirect
                to={{
                  pathname: `/classroom/rostering/${user.userProfile?.providerName}/authenticatedRoute`,
                  state: { nextPathname: location.pathname },
                }}
              />
            )}
          />
        );
      }
    }

    // Takween student routing
    if (userProfile?.licenseLevel === 'TAKWEEN' && isStudent(userProfile)) {
      const grade = userProfile.grade || 0;

      // PK-5 students: Show "not available" message
      if (grade <= 5) {
        return (
          <Route
            render={() => (
              <Redirect
                to={{
                  pathname: '/takween-not-available',
                  state: { grade },
                }}
              />
            )}
          />
        );
      }
      // Grades 6-12: Continue to student portal (normal flow continues)
    }

    // Check if user is eligible for survey
    const isEligible = isUserEligibleForSurvey(userProfile);
    const shouldSkip = isSkipCheckRouteForSurvey();

    console.log('[AuthenticatedRoute] Survey check:', {
      path: location.pathname,
      isEligible,
      shouldSkip,
      userType: userProfile?.type,
      hasFlags: !!userProfile?.flags,
      isShowSurvey: userProfile?.flags?.isShowSurvey,
      hasSurveyParams: !!userProfile?.surveyParameters,
      campaignId: userProfile?.surveyParameters?.campaignId,
      redirectCount: surveyRedirectCount.current,
      lastRedirectPath: lastSurveyRedirectPath.current,
      willRedirect: isEligible && !shouldSkip,
    });

    if (isEligible && !shouldSkip) {
      // Safety: Prevent infinite redirect loops
      // Track consecutive redirects from the same path
      if (lastSurveyRedirectPath.current === location.pathname) {
        // We're trying to redirect from the same path again
        surveyRedirectCount.current += 1;

        if (surveyRedirectCount.current > 3) {
          console.error('[AuthenticatedRoute] Maximum survey redirect limit reached', {
            userType: userProfile?.type,
            campaignId: userProfile?.surveyParameters?.campaignId,
            currentPath: location.pathname,
            attempts: surveyRedirectCount.current,
          });

          // Break the loop - allow navigation to intended destination
          // Reset counter so it doesn't affect future navigations
          surveyRedirectCount.current = 0;
          lastSurveyRedirectPath.current = null;
          return <Route {...rest}>{children}</Route>;
        }
      } else {
        // Different path, reset counter
        surveyRedirectCount.current = 1; // Start at 1 since we're about to redirect
        lastSurveyRedirectPath.current = location.pathname;
      }

      return (
        <Route
          render={({ location }) => (
            <Redirect
              to={{
                pathname: '/survey-choice',
                state: {
                  nextPathname: location.pathname,
                  surveyParameters: userProfile.surveyParameters,
                },
              }}
            />
          )}
        />
      );
    }

    // Not eligible or should skip - reset counter
    if (lastSurveyRedirectPath.current !== null) {
      surveyRedirectCount.current = 0;
      lastSurveyRedirectPath.current = null;
    }

    return <Route {...rest}>{children}</Route>;

    //
    // these routes should not trigger a prompt for missing
    // information, otherwise there'll be a continuous loop
    //
    function isSkipCheckRouteForAutoSync() {
      return (
        location.pathname.toLowerCase().includes('/classroom/rostering') ||
        location.pathname.toLowerCase().includes('/moreinformation/school')
      );
    }

    function isSkipCheckRouteForMissingSchool() {
      return ['/moreinformation/school'].includes(location.pathname.toLowerCase());
    }

    function isSkipCheckRouteForSurvey() {
      const path = location.pathname.toLowerCase();
      return (
        path === '/survey' ||
        path === '/survey-choice' ||
        path.includes('/classroom/rostering') ||
        path.includes('/moreinformation/school')
      );
    }

    function isUserEligibleForSurvey(userProfile: UserProfileType | null): boolean {
      if (!userProfile?.flags?.isShowSurvey || !userProfile?.surveyParameters) {
        console.log('[AuthenticatedRoute] User not eligible - missing flags or params:', {
          hasUserProfile: !!userProfile,
          hasFlags: !!userProfile?.flags,
          hasShowSurveyFlag: !!userProfile?.flags?.isShowSurvey,
          hasSurveyParameters: !!userProfile?.surveyParameters,
        });
        return false;
      }

      const userId = userProfile.userId;
      const campaignId = userProfile.surveyParameters.campaignId;

      // Check backend completion status
      const completedCampaigns = userProfile.history?.campaignCompletions || [];
      const isCompletedBackend = completedCampaigns.some((c) => c.campaignId === campaignId);

      // Check frontend choice status (now per-user)
      const hasChoiceFrontend = hasUserMadeChoice(userId, campaignId);

      // Check temporary frontend tracking (now per-user)
      let isCompletedTemp = false;
      try {
        const allCompletions = JSON.parse(getLocalStorage('completedSurveys') || '{}');
        const userCompletions = allCompletions[userId] || [];
        isCompletedTemp = userCompletions.some((c: any) => c.campaignId === campaignId);
      } catch (error) {
        console.error('Error checking temporary survey completion:', error);
      }

      const isEligibleResult = !(isCompletedBackend || hasChoiceFrontend || isCompletedTemp);

      // Parse and show the actual choice data for this user
      let choiceData = null;
      try {
        const allChoices = JSON.parse(getLocalStorage('surveyChoices') || '{}');
        const userChoices = allChoices[userId] || {};
        choiceData = userChoices[campaignId];
      } catch (e) {
        console.error('Error parsing survey choices:', e);
      }

      console.log('[AuthenticatedRoute] Eligibility details:', {
        userId,
        campaignId,
        isCompletedBackend,
        hasChoiceFrontend,
        isCompletedTemp,
        result: isEligibleResult,
        choiceData,
        choiceExpired: choiceData ? choiceData.expiresAt <= Date.now() : null,
        currentTime: Date.now(),
      });

      return isEligibleResult;
    }
  }
};

export default AuthenticatedRoute;
